import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Button } from '@components/Button';
import emailService from '@services/emailService';
import { EMAIL_STATUS_LABELS, EMAIL_TEMPLATE_LABELS, type EmailOutboxItem, type EmailStatus } from '@/types/planning';
import { formatDateTimeBR } from '@/utils/formatters';

const STATUS_BADGE: Record<EmailStatus, string> = {
  pending: 'badge-warning',
  sending: 'badge-info',
  sent: 'badge-success',
  failed: 'badge-danger',
};

const STATUS_OPTIONS: { value: EmailStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: EMAIL_STATUS_LABELS.pending },
  { value: 'sending', label: EMAIL_STATUS_LABELS.sending },
  { value: 'sent', label: EMAIL_STATUS_LABELS.sent },
  { value: 'failed', label: EMAIL_STATUS_LABELS.failed },
];

const LIMIT = 100;

const statusBadge = (s: string) => (
  <span className={STATUS_BADGE[s as EmailStatus] ?? 'badge-neutral'}>{EMAIL_STATUS_LABELS[s as EmailStatus] ?? s}</span>
);
const templateLabel = (key: string) => EMAIL_TEMPLATE_LABELS[key] ?? key;

/**
 * Administração → E-mails (só admin): caixa de saída do app (GET /admin/emails). Não há envio
 * manual — o worker interno envia pelo Microsoft Graph com tentativas e backoff; aqui só se
 * acompanha a fila: para quem, assunto, template, situação, tentativas, próxima tentativa, enviado
 * em e o último erro.
 */
export const EmailsAdminView: React.FC = () => {
  const [items, setItems] = useState<EmailOutboxItem[]>([]);
  const [status, setStatus] = useState<EmailStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await emailService.list(status, LIMIT));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const errorCell = (e: EmailOutboxItem) =>
    e.last_error ? (
      <span className="text-red-700 block max-w-[16rem] truncate" title={e.last_error}>
        {e.last_error}
      </span>
    ) : (
      <span className="text-slate-400">—</span>
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Mail size={20} className="text-slate-500" /> E-mails
        </h1>
        <div className="grid grid-cols-1 sm:flex items-center gap-2 w-full sm:w-auto">
          <select className="select-field sm:w-44" value={status} onChange={(e) => setStatus(e.target.value as EmailStatus | '')} aria-label="Situação">
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button variant="outline" icon={<RefreshCw />} onClick={load} loading={loading}>
            Atualizar
          </Button>
        </div>
      </div>
      <p className="text-small">
        Caixa de saída dos e-mails que o Plan envia (boas-vindas, novo cadastro, acesso estendido, contas a agendar) por{' '}
        <strong>suporte@giannotti.com.br</strong>. Falha temporária volta para a fila com nova tentativa em 1, 5 e 30 min, 2 h e 12 h; na 6ª
        falha, ou em erro permanente, fica como <strong>Falhou</strong>. Enviados somem depois de 90 dias. Os {LIMIT} mais recentes.
      </p>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-500 py-6">Nenhum e-mail na caixa de saída.</p>}
        {items.map((e) => (
          <div key={e.email_id} className="card p-3 space-y-1.5 text-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800 [overflow-wrap:anywhere]">{e.subject}</p>
                <p className="text-small [overflow-wrap:anywhere]">
                  {e.to_name ? `${e.to_name} · ` : ''}
                  {e.to_email}
                </p>
              </div>
              {statusBadge(e.status)}
            </div>
            <p className="text-small">
              {templateLabel(e.template_key)} · criado {formatDateTimeBR(e.created_at)}
            </p>
            <p className="text-small">
              {e.attempts ?? 0} {e.attempts === 1 ? 'tentativa' : 'tentativas'}
              {e.sent_at ? ` · enviado ${formatDateTimeBR(e.sent_at)}` : e.next_attempt_at && e.status !== 'sent' ? ` · próxima ${formatDateTimeBR(e.next_attempt_at)}` : ''}
            </p>
            {e.last_error && (
              <p className="text-small text-red-700 [overflow-wrap:anywhere]" title={e.last_error}>
                {e.last_error.length > 160 ? `${e.last_error.slice(0, 160)}…` : e.last_error}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Para</th>
                <th>Assunto</th>
                <th>Template</th>
                <th>Situação</th>
                <th className="text-right">Tentativas</th>
                <th>Próxima tentativa</th>
                <th>Enviado em</th>
                <th>Erro</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="table-cell text-center text-slate-500 py-8">
                    Nenhum e-mail na caixa de saída.
                  </td>
                </tr>
              )}
              {items.map((e) => (
                <tr key={e.email_id} className={clsx('table-row', e.status === 'failed' && 'bg-red-50/40')}>
                  <td className="table-cell">
                    {e.to_name && <p className="font-medium text-slate-800">{e.to_name}</p>}
                    <p className={clsx('[overflow-wrap:anywhere]', e.to_name ? 'text-small' : 'text-slate-800')}>{e.to_email}</p>
                  </td>
                  <td className="table-cell">
                    <span className="block max-w-[20rem] truncate" title={e.subject}>
                      {e.subject}
                    </span>
                    <span className="text-small">criado {formatDateTimeBR(e.created_at)}</span>
                  </td>
                  <td className="table-cell whitespace-nowrap" title={e.template_key}>
                    {templateLabel(e.template_key)}
                  </td>
                  <td className="table-cell">{statusBadge(e.status)}</td>
                  <td className="table-cell text-right tabular-nums">{e.attempts ?? 0}</td>
                  <td className="table-cell whitespace-nowrap">
                    {e.status === 'sent' || !e.next_attempt_at ? <span className="text-slate-400">—</span> : formatDateTimeBR(e.next_attempt_at)}
                  </td>
                  <td className="table-cell whitespace-nowrap">{e.sent_at ? formatDateTimeBR(e.sent_at) : <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell">{errorCell(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
